// Keep this file free of zod: the Explorer bundles it client-side and
// must not pull the zod runtime in with it. Schemas live in metrics.ts.

/**
 * Wire sentinel: `key_string` of the "Other" remainder series that the
 * composed `metrics.getSectorChartData` endpoint appends after the top-N.
 * Lives here so the API that writes it and the chart pipeline that matches
 * on it share one literal (the response types themselves cross the boundary
 * via tRPC inference, but this constant does not have to).
 */
export const OTHER_SERIES_KEY = "other";

/**
 * `reference_asset_id` slugs of pre-IPO equities. Shared so the API's
 * `rwa-segment` group-by (which splits them out of stocks in SQL) and the
 * tokenized-assets pages (which filter on them) read one list.
 */
export const PRE_IPO_REFERENCE_ASSET_IDS = [
	"openai",
	"spacex",
	"kalshi",
	"xai",
	"polymarket",
	"anthropic",
	"anduril",
] as const;

/**
 * `rwa_segment` values the `rwa-segment` group-by produces. Three fixed
 * bands; every other asset keeps its `reference_asset_id` behind `ra:`.
 * Band `key_string`s are `seg:<rwa_segment>`.
 */
export const RWA_SEGMENT_ETFS = "rwa-etfs";
export const RWA_SEGMENT_PRE_IPO = "rwa-pre-ipo";
export const RWA_SEGMENT_STOCKS = "rwa-stocks";
export const RWA_SEGMENT_REFERENCE_ASSET_PREFIX = "ra:";
export const RWA_SEGMENT_KEY_PREFIX = "seg:";

export type RwaFixedSegment =
	| typeof RWA_SEGMENT_ETFS
	| typeof RWA_SEGMENT_PRE_IPO
	| typeof RWA_SEGMENT_STOCKS;

/** `reference_asset_id` behind a `ra:<id>` segment; null for the fixed bands. */
export function rwaSegmentReferenceAssetId(
	segment: string | null | undefined,
): string | null {
	return segment?.startsWith(RWA_SEGMENT_REFERENCE_ASSET_PREFIX)
		? segment.slice(RWA_SEGMENT_REFERENCE_ASSET_PREFIX.length)
		: null;
}

/**
 * Metrics whose chart timeseries queries must pass `includeSelf: true`
 * (include the issuing project's own rows in the breakdown). Shared so the
 * client chart pipeline (`useSeries`) and the API's composed chart endpoint
 * (`metrics.getSectorChartData`) apply the exact same quirk.
 */
const INCLUDE_SELF_METRIC_IDS: ReadonlySet<string> = new Set([
	"cbbtc_outstanding_supply",
]);

export function metricIncludesSelf(metricId: string): boolean {
	return INCLUDE_SELF_METRIC_IDS.has(metricId);
}
